import Styles from './PostImages.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { MediaObject, SelectedImage } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { DownloadMedia, ImageAdd, MoreDots, Trash2 } from 'blaze-ui/icons/20'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useDownloadMediaFile } from 'hooks/useDownloadMediaFile'
import { ImageWithProgressPercentage } from 'proseShared/prompts/MediaUploadPrompt/ImageWithProgressPercentage'
import { noop } from 'lodash'
import { Carousel } from 'components/Carousel/Carousel'
import { Provider } from 'components/ImagePickerModal/Types'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { maxFiles, useArticlePostPreviewContext } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { ProgressBar } from 'components/ArticlePostPreview/Previews/components/ProgressBar/ProgressBar'
import { useMediaFileUploadProgress } from 'components/ArticlePostPreview/useMediaFileUploadProgress'
import { ImagePickerContextState } from 'components/ImagePickerModal/ImagePickerContextTypes'

interface PostImageItemProps {
  file: MediaObject
  onRemove?: () => void
  readOnly?: boolean
  withActions?: boolean
}

interface PostImagesProps {
  files: MediaObject[]
  onRemove: (index: number) => void
  uploadImages: (images: File[], context: ImagePickerContextState) => Promise<SelectedImage[]>
  provider?: ExternalIntegrationEnum
  onConfirm?: (images: SelectedImage[], context: ImagePickerContextState) => void
}

export const PostImageItem = ({ file, onRemove, withActions = true }: PostImageItemProps) => {
  const { downloadFile } = useDownloadMediaFile()
  const url = file.thumbnail_url || file.source_url

  return (
    <div className={Styles.postImage}>
      {url && (
        <>
          <ImageWithProgressPercentage alt="preview" src={url} onError={noop} useOriginalSrc />
          {file.duration && file.duration > 0 && <VideoDuration duration={file.duration} />}
        </>
      )}
      {withActions && (
        <div className={Styles.imageActions}>
          <DropdownTrigger placement="bottom-start" popoverClassName={Styles.popoverWrapper} containFocus={false}>
            <IconButton variant="secondary" size="sm" icon={MoreDots} />
            {(close) => (
              <div className={Styles.actionsPopover}>
                <div
                  className={Styles.action}
                  onClick={() => {
                    downloadFile(file)
                    close()
                  }}
                >
                  <DownloadMedia />
                  <Text variant="largeList">Download Media</Text>
                </div>
                <div
                  className={Styles.action}
                  onClick={() => {
                    onRemove?.()
                    close()
                  }}
                >
                  <Trash2 />
                  <Text variant="largeList">Remove from this Post</Text>
                </div>
              </div>
            )}
          </DropdownTrigger>
        </div>
      )}
    </div>
  )
}

export const PostImages = observer(({ files, onRemove, provider, uploadImages, onConfirm }: PostImagesProps) => {
  const { drawersStore } = useRootStore()
  const maxFilesToUpload = provider ? maxFiles[provider] : 0
  const { onUploadStart } = useMediaFileUploadProgress(drawersStore)
  const {
    state: { uploadingMediaFilesMetadata },
  } = useArticlePostPreviewContext()

  return (
    <Carousel>
      {files.map((file, index) => (
        <PostImageItem key={`${file.id}-${index}`} file={file} onRemove={() => onRemove(index)} />
      ))}

      {uploadingMediaFilesMetadata.map((metadata, index) => (
        <div className={Styles.postImage} key={`uploading-media-file-${index}`}>
          <img className={Styles.uploadPreview} src={metadata.thumbnail_url} alt="video frame" />
          <ProgressBar progress={metadata.progress || 0} />
        </div>
      ))}

      {files.length < maxFilesToUpload && (
        <button
          className={Styles.addMedia}
          disabled={uploadingMediaFilesMetadata.length > 0}
          onClick={() =>
            drawersStore.openDrawer(ImagePickerDrawer, {
              uploadImages,
              maxCount: maxFilesToUpload - files.length,
              hiddenProviders: new Set<Provider>(['gifs']),
              onConfirm,
              accept: ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES,
              onUploadStart,
            })
          }
        >
          <ImageAdd color="var(--dark-80)" />
          <Text variant="smallList" color="var(--dark-80)">
            Add Images or Videos
          </Text>
        </button>
      )}
    </Carousel>
  )
})
